import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import axios from 'axios';
import { createSocketIOWithFailover } from '../utils/serverFailover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SessionLobby({ user }) {
  const { pin } = useParams();
  const navigate = useNavigate();
  const [socket, setSocket] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [showClassroomModal, setShowClassroomModal] = useState(false);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [assignmentTitle, setAssignmentTitle] = useState('');
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [quizTitle, setQuizTitle] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('Conectando...');
  const [connectionError, setConnectionError] = useState(null);

  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        const sessionResponse = await axios.get(`${BACKEND_URL}/api/sessions/pin/${pin}`);
        setSessionId(sessionResponse.data.id);
        
        const quizResponse = await axios.get(`${BACKEND_URL}/api/quizzes/${sessionResponse.data.quiz_id}`);
        setQuizTitle(quizResponse.data.title);
        setAssignmentTitle(quizResponse.data.title);
      } catch (error) {
        console.error('Error fetching session data:', error);
      }
    };

    fetchSessionData();

    // Initialize socket with failover
    let newSocket = null;
    const initializeConnection = async () => {
      try {
        setConnectionStatus('Conectando...');
        setConnectionError(null);

        newSocket = await createSocketIOWithFailover(
          io,
          user?.id || 'teacher',
          (connectedSocket, server) => {
            console.log(`Connected to server via ${server.name}`);
            setConnectionStatus(`Conectado a ${server.name}`);
            setSocket(connectedSocket);

            connectedSocket.on('connect', () => {
              console.log('Socket.IO connection established');
              console.log('Joining as teacher with PIN:', pin);
              connectedSocket.emit('join_teacher', { pin });
            });

            connectedSocket.on('session_state', (data) => {
              console.log('Session state received:', data);
              setParticipants(data.participants || []);
            });

            connectedSocket.on('participant_joined', (data) => {
              console.log('Participant joined:', data);
              setParticipants(prev => [...prev, data.participant]);
            });

            connectedSocket.on('participant_left', (data) => {
              console.log('Participant left:', data);
              setParticipants(prev => prev.filter(p => {
                const pEmail = p.email || p.id;
                const leftEmail = data.participant.email || data.participant.id;
                return pEmail !== leftEmail;
              }));
            });

            connectedSocket.on('error', (data) => {
              console.error('Socket error:', data);
            });

            // Emit join immediately if already connected
            if (connectedSocket.connected) {
              connectedSocket.emit('join_teacher', { pin });
            }
          },
          (error) => {
            console.error('Socket error:', error);
          },
          (status) => {
            setConnectionStatus(status);
          }
        );
      } catch (error) {
        console.error('Failed to connect:', error);
        setConnectionError(error.message);
        setConnectionStatus('Error de conexión');
      }
    };

    initializeConnection();

    return () => {
      if (newSocket) {
        newSocket.close();
      }
    };
  }, [pin]);

  const handleStartGame = () => {
    if (participants.length === 0) {
      alert('Espera a que al menos un estudiante se una');
      return;
    }
    if (socket) {
      socket.emit('start_game', { pin });
      navigate(`/session/${pin}/control`);
    }
  };

  const handleOpenClassroomModal = async () => {
    setLoadingCourses(true);
    setShowClassroomModal(true);
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/classroom/courses/${user.id}`);
      setCourses(response.data.courses || []); // Access the courses array properly
    } catch (error) {
      console.error('Error fetching courses:', error);
      alert('Error al cargar cursos de Classroom. ¿Has conectado tu cuenta?');
      setShowClassroomModal(false);
    } finally {
      setLoadingCourses(false);
    }
  };

  const handleCreateAssignment = async () => {
    if (!selectedCourse) {
      alert('Por favor selecciona un curso');
      return;
    }

    if (!assignmentTitle.trim()) {
      alert('Por favor ingresa un título para la tarea');
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/classroom/create-assignment`, {
        teacher_id: user.id,
        course_id: selectedCourse,
        session_id: sessionId,
        title: assignmentTitle,
        description: `Quiz: ${quizTitle}`,
        max_points: 100
      });

      alert('✅ Tarea creada en Google Classroom!');
      setShowClassroomModal(false);
    } catch (error) {
      console.error('Error creating assignment:', error);
      alert('Error al crear tarea en Classroom');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary to-purple-900 text-white" data-testid="session-lobby">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <h1 className="text-6xl font-bold mb-4">PIN: {pin}</h1>
          <p className="text-2xl text-purple-200">Los estudiantes deben ingresar este PIN para unirse</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Participants List */}
          <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-6">
              Participantes ({participants.length})
            </h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {participants.length === 0 ? (
                <div className="text-center py-12 text-purple-200">
                  <div className="text-6xl mb-4">⏳</div>
                  <p>Esperando a que los estudiantes se unan...</p>
                </div>
              ) : (
                participants.map((participant, index) => (
                  <div key={index} className="bg-white bg-opacity-20 rounded-lg p-4 flex items-center gap-3" data-testid={`participant-${index}`}>
                    {participant.picture && (
                      <img src={participant.picture} alt={participant.name} className="w-12 h-12 rounded-full" />
                    )}
                    <span className="font-semibold">{participant.name}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="space-y-6">
            <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-8">
              <h3 className="text-xl font-bold mb-4">📱 Instrucciones para Estudiantes</h3>
              <ol className="space-y-3 text-purple-100">
                <li>1. Ir a la aplicación</li>
                <li>2. Ingresar el PIN: <span className="font-bold text-2xl">{pin}</span></li>
                <li>3. Esperar a que comience el quiz</li>
              </ol>
            </div>

            <button
              onClick={handleStartGame}
              disabled={participants.length === 0}
              className="w-full px-8 py-6 bg-success text-white text-xl font-bold rounded-2xl hover:bg-green-600 transition-all shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="start-game-btn"
            >
              🚀 Iniciar Quiz
            </button>

            <button
              onClick={handleOpenClassroomModal}
              className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition flex items-center justify-center gap-2"
              data-testid="link-classroom-btn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L1 7l4 2.18v6L12 19l7-3.82v-6l2-1.09V17h2V7L12 2zm6.82 6L12 11.72 5.18 8 12 4.28 18.82 8zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
              </svg>
              Vincular con Google Classroom
            </button>

            <button
              onClick={() => navigate('/dashboard')}
              className="w-full px-6 py-3 bg-white bg-opacity-20 text-white font-semibold rounded-xl hover:bg-opacity-30 transition"
            >
              Cancelar y Volver
            </button>
          </div>
        </div>
      </div>

      {/* Classroom Modal */}
      {showClassroomModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowClassroomModal(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Vincular con Google Classroom</h3>
            <p className="text-sm text-gray-600 mb-6">Elige el curso donde quieres crear esta tarea</p>
            
            {loadingCourses ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="mt-4 text-gray-600">Cargando cursos...</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título de la Tarea
                  </label>
                  <input
                    type="text"
                    value={assignmentTitle}
                    onChange={(e) => setAssignmentTitle(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 bg-white"
                    placeholder="Nombre de la tarea"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seleccionar Curso
                  </label>
                  <select
                    value={selectedCourse}
                    onChange={(e) => setSelectedCourse(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 bg-white"
                  >
                    <option value="">-- Selecciona un curso --</option>
                    {Array.isArray(courses) && courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Nota:</strong> Se creará una tarea con 100 puntos máximos. Las calificaciones se enviarán automáticamente al finalizar el quiz.
                  </p>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleCreateAssignment}
                    className="flex-1 px-4 py-3 bg-primary text-white rounded-lg hover:bg-purple-700 transition font-semibold"
                  >
                    Crear Tarea
                  </button>
                  <button
                    onClick={() => setShowClassroomModal(false)}
                    className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SessionLobby;
